import type { BrainstormOptionItem } from '@frontend/api-types';
import { XStack } from '../Primitives/Containers/Stacks/XStack';
import { YStack } from '../Primitives/Containers/Stacks/YStack';
import { Icon } from '../Primitives/Icon/Icon';
import { Text } from '../Primitives/Texts/Text';

export type BrainstormOptionCardProps = {
  label: string;
  onClick?: () => void;
  option: BrainstormOptionItem;
  phase: 'final' | 'initial';
};

export const BrainstormOptionCard = ({ label, onClick, option, phase }: BrainstormOptionCardProps) => {
  const { attributes, description, imageUrl, price, title, url } = option;
  const attributeEntries = Object.entries(attributes);

  return (
    <YStack
      accessibilityLabel={onClick ? `Select ${option.title}` : undefined}
      bg="cream50"
      canFocus={!!onClick}
      className={`p-sm ${onClick ? 'cursor-pointer transition-colors hover:bg-cream-100' : ''}`}
      onClick={onClick}
      radius="lg"
    >
      <XStack className="gap-sm">
        {imageUrl ? (
          <img
            alt={title}
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className="size-20 rounded-md object-cover shrink-0"
            src={imageUrl}
          />
        ) : null}
        <YStack className="flex-1 gap-xs">
          <XStack alignItems="center" className="gap-xs">
            {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
            <Text className="bg-cream-200 px-xs py-xxs rounded text-xs" type="caption">
              {label}
            </Text>
            {phase === 'final' && label === 'Best' ? <Icon color="warning" name="IconStar" size={14} /> : null}
          </XStack>
          <Text className="text-neutral-950 font-medium" type="p-sm">
            {title}
          </Text>
          {price ? (
            <Text color="tertiary" type="caption">
              {price}
            </Text>
          ) : null}
          <Text color="secondary" type="caption">
            {description}
          </Text>
          {attributeEntries.length > 0 ? (
            <Text color="tertiary" type="caption">
              {attributeEntries.map(([k, v]) => `${k}: ${v}`).join(' • ')}
            </Text>
          ) : null}
          {url ? (
            <a
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="text-xs text-blue-600 hover:underline"
              href={url}
              onClick={(e) => e.stopPropagation()}
              rel="noopener noreferrer"
              target="_blank"
            >
              View details
            </a>
          ) : null}
        </YStack>
      </XStack>
    </YStack>
  );
};
